import { useState, useRef, useEffect } from 'react';
import { EntrepriseSettings } from '../../mocks/entreprise';
import { Commande, statutLabels } from '../../mocks/commandes';
import { VenteTissuRecord } from '../../mocks/ventes';
import { api } from '../../services/api';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { formatPrice } from '../../utils/format';

type FactureData =
  | { type: 'commande'; data: Commande }
  | { type: 'vente'; data: VenteTissuRecord };

interface FactureModalProps {
  factureData: FactureData;
  entreprise: EntrepriseSettings;
  onClose: () => void;
  autoShareWhatsApp?: boolean;
}

function formatDate(d: string) {
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
}

function fmt(n: number | string, devise = 'FCFA') {
  return formatPrice(n, true);
}

function generateNumero(id: string) {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  return `FAC-${y}${m}-${id}`;
}

export default function FactureModal({ factureData, entreprise, onClose, autoShareWhatsApp }: FactureModalProps) {
  const printRef = useRef<HTMLDivElement>(null);
  const [downloading, setDownloading] = useState(false);
  const [sharing, setSharing] = useState(false);

  const isCommande = factureData.type === 'commande';
  const commande = isCommande ? (factureData.data as Commande) : null;
  const vente = !isCommande ? (factureData.data as VenteTissuRecord) : null;

  // FIX BUG #8: Fallbacks sécurisés pour tous les champs potentiellement null
  const clientNom = (isCommande ? (commande?.client?.nom || 'Client') : (vente?.clientNom || 'Client')) || 'Client';
  const clientTel = (isCommande ? (commande?.client?.telephone || '') : (vente?.clientTelephone || '')) || '';
  const dateDoc   = (isCommande ? (commande?.dateCommande || commande?.date_commande || '') : (vente?.date || vente?.created_at || '')) || new Date().toISOString();
  const idDoc     = (isCommande ? (commande?.id || 'N/A') : (vente?.id || 'N/A')) || 'N/A';
  const numFacture = generateNumero(String(idDoc));

  const handlePrint = () => {
    window.print();
  };

  const generateBlob = async (): Promise<Blob | null> => {
    if (!printRef.current) return null;
    try {
      const canvas = await html2canvas(printRef.current, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
      return new Promise((resolve) => {
        canvas.toBlob((blob) => resolve(blob), 'application/pdf', 0.95);
        // html2canvas toBlob is actually image/png/jpeg. We need an image blob for PDF.
        // Actually we just need the canvas as image data for jsPDF.
      });
    } catch (e) {
      console.error(e);
      return null;
    }
  };

  const handlePDF = async () => {
    if (!printRef.current) return;
    setDownloading(true);
    try {
      const element = printRef.current;
      const canvas = await html2canvas(element, { 
        scale: 2, 
        useCORS: true, 
        backgroundColor: '#ffffff',
        width: 794,
        windowWidth: 794 
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = (canvas.height * pdfW) / canvas.width;
      pdf.addImage(imgData, 'PNG', 0, 0, pdfW, pdfH);
      pdf.save(`${numFacture}.pdf`);
    } catch (e) {
      console.error(e);
    } finally {
      setDownloading(false);
    }
  };

  const handleWhatsAppPDF = async () => {
    if (!printRef.current) return;
    setSharing(true);
    
    // 1. Saisie/validation interactive du numéro de téléphone
    let targetPhone = clientTel.trim();
    const promptMsg = targetPhone 
      ? `Confirmez ou modifiez le numéro de téléphone WhatsApp du client :`
      : `Ce client n'a pas de numéro enregistré. Saisissez son numéro de téléphone WhatsApp :`;
    
    const enteredPhone = prompt(promptMsg, targetPhone);
    if (enteredPhone === null) {
      // Annulation par l'utilisateur
      setSharing(false);
      return;
    }
    
    targetPhone = enteredPhone.trim();
    if (!targetPhone) {
      alert("Un numéro de téléphone est requis pour envoyer la facture via WhatsApp.");
      setSharing(false);
      return;
    }

    let waWindow: Window | null = null;
    try {
      // 2. Générer le PDF en mémoire
      const element = printRef.current;
      const canvas = await html2canvas(element, { 
        scale: 2, 
        useCORS: true, 
        backgroundColor: '#ffffff',
        width: 794,
        windowWidth: 794
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = (canvas.height * pdfW) / canvas.width;
      pdf.addImage(imgData, 'PNG', 0, 0, pdfW, pdfH);
      const pdfBlob = pdf.output('blob');

      // 3. Essai de partage natif de fichier (si mobile/tablette compatible)
      const pdfFile = new File([pdfBlob], `${numFacture}.pdf`, { type: 'application/pdf' });
      const canShareDirect = !!(navigator.share && navigator.canShare && navigator.canShare({ files: [pdfFile] }));
      
      if (canShareDirect) {
        try {
          await navigator.share({
            files: [pdfFile],
            title: `Facture ${numFacture}`,
            text: `Bonjour ${clientNom},\nVoici votre facture ${numFacture} de l'Atelier Bassoum.`
          });
          if (autoShareWhatsApp) {
            onClose();
          }
          return; // Succès du partage direct
        } catch (shareErr: any) {
          if (shareErr.name === 'AbortError') {
            console.log("Partage direct annulé par l'utilisateur.");
            return;
          }
          console.warn("Échec du partage direct, repli vers wa.me :", shareErr);
        }
      }

      // 4. Si partage natif non dispo/échoué, repli vers wa.me avec hébergement PDF
      // Ouverture immédiate de l'onglet vierge pour contourner le bloqueur de popup
      waWindow = window.open('about:blank', '_blank');

      const token = localStorage.getItem('atelier_bassoum_token') || '';
      const uploadRes = await api.uploadFacturePdf(token, pdfBlob, `${numFacture}.pdf`);
      
      if (uploadRes.status === 'success' && uploadRes.url) {
        // Nettoyage et formatage du numéro
        let phone = targetPhone.replace(/\s+/g, '').replace(/[-\(\)]/g, '');
        if (phone.startsWith('00')) {
          phone = phone.substring(2);
        }
        const cleanPhone = phone.startsWith('+') ? phone.replace('+', '') : (phone.startsWith('223') ? phone : `223${phone}`);
        
        // Calcul des montants pour le bilan d'achat
        let total = 0;
        let remise = 0;
        let acompte = 0;
        let reste = 0;

        if (isCommande && commande) {
          total = Number(commande.paiement?.total || 0);
          remise = Number(commande.paiement?.remise || 0);
          acompte = Number(commande.paiement?.acompte || 0);
          reste = Number(commande.paiement?.reste || 0);
        } else if (vente) {
          total = Number(vente.total || 0);
          remise = Number(vente.remise || 0);
          acompte = Number(vente.acompte ?? vente.montantRecu ?? 0);
          reste = Number(vente.reste || 0);
        }

        // Construction du message WhatsApp textuel enrichi
        let msgText = `*Atelier Bassoum*\n`;
        msgText += `Bonjour ${clientNom},\n\n`;
        msgText += `Voici le bilan d'achat pour votre facture ${numFacture} :\n`;
        msgText += `• Total brut : ${fmt(total)}\n`;
        if (remise > 0) {
          msgText += `• Remise : ${fmt(remise)}\n`;
          msgText += `• Net à payer : ${fmt(total - remise)}\n`;
        }
        msgText += `• Acompte versé : ${fmt(acompte)}\n`;
        msgText += `• Reste à payer : *${fmt(reste)}*\n\n`;
        msgText += `Téléchargez votre facture PDF ici : ${uploadRes.url}\n\n`;
        msgText += `Merci pour votre confiance !`;

        const message = encodeURIComponent(msgText);
        const waUrl = `https://wa.me/${cleanPhone}?text=${message}`;

        if (waWindow) {
          waWindow.location.href = waUrl;
        } else {
          window.open(waUrl, '_blank');
        }

        if (autoShareWhatsApp) {
          onClose();
        }
      } else {
        throw new Error("Erreur de réponse du serveur d'upload.");
      }
    } catch (e) {
      console.error(e);
      if (waWindow) {
        waWindow.close();
      }
      alert("Erreur lors de la génération du partage.");
    } finally {
      setSharing(false);
    }
  };

  useEffect(() => {
    if (autoShareWhatsApp) {
      const timer = setTimeout(() => {
        handleWhatsAppPDF();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [autoShareWhatsApp]);

  // Use central proxy helper for the logo URL
  // Extraction robuste supportant logoUrl (frontend) et logo_url (backend)
  const logoRaw = entreprise?.logoUrl || (entreprise as any)?.logo_url;
  const logoUrl = logoRaw ? api.getProxyUrl(logoRaw) : null;
  
  const isAnnulee = isCommande && commande?.statut === 'ANNULEE';
  const [imgError, setImgError] = useState(false);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl my-auto relative overflow-hidden" onClick={e => e.stopPropagation()}>
        {sharing && autoShareWhatsApp && (
          <div className="absolute inset-0 bg-white/95 backdrop-blur-sm z-50 flex flex-col items-center justify-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
            <p className="text-gray-800 font-bold text-base">Génération & Envoi de la facture...</p>
            <p className="text-xs text-gray-400">Votre facture PDF est en cours de création et sera partagée via WhatsApp.</p>
          </div>
        )}
        {/* Toolbar */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 print:hidden">
          <div>
            <h3 className="font-playfair font-bold text-lg text-gray-900">Facture / Reçu</h3>
            <p className="text-xs text-gray-400">{numFacture}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
                onClick={handleWhatsAppPDF}
                disabled={sharing}
                className="flex items-center gap-2 text-xs px-3 py-2 rounded-xl bg-green-500 text-white hover:bg-green-600 cursor-pointer font-semibold whitespace-nowrap disabled:opacity-70"
                title="Envoyer par WhatsApp"
              >
                <div className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-whatsapp-line" /></div>
                {sharing ? 'Envoi...' : 'WhatsApp'}
              </button>
            <button
              onClick={handlePrint}
              className="hidden md:flex items-center gap-2 text-xs px-3 py-2 rounded-xl bg-gray-100 text-gray-600 hover:bg-gray-200 cursor-pointer font-semibold whitespace-nowrap"
            >
              <div className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-printer-line" /></div>
              Imprimer
            </button>
            <button
              onClick={handlePDF}
              disabled={downloading}
              className="flex items-center gap-2 text-xs px-3 py-2 rounded-xl bg-foret-500 text-white hover:bg-foret-600 cursor-pointer font-semibold whitespace-nowrap disabled:opacity-70"
            >
              <div className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-download-line" /></div>
              {downloading ? 'Export…' : 'PDF'}
            </button>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-gray-200 cursor-pointer text-gray-500"
              title="Fermer"
            >
              <i className="ri-close-line" />
            </button>
          </div>
        </div>

        <div ref={printRef} className="px-4 py-5 md:px-8 md:py-7 bg-white relative overflow-hidden">
          {isAnnulee && (
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center opacity-[0.03] rotate-[-35deg] select-none">
              <span className="text-9xl font-black border-8 border-red-500 text-red-500 px-10 py-5 rounded-full">ANNULÉE</span>
            </div>
          )}
          <style>
            {`
              .dynamic-primary-bg { background-color: ${entreprise.couleurPrimaire}; }
              .dynamic-primary-text { color: ${entreprise.couleurPrimaire}; }
              .dynamic-primary-light-bg { background-color: ${entreprise.couleurPrimaire}15; }
              .dynamic-primary-header-bg { background-color: ${entreprise.couleurPrimaire}12; }
            `}
          </style>

          {/* Company header */}
          <div className="flex items-start justify-between mb-8 pb-6 border-b-2 border-gray-100">
             <div className="flex items-start gap-3 md:gap-4">
            {logoUrl && !imgError ? (
              <div className="w-12 h-12 md:w-16 md:h-16 rounded-xl overflow-hidden flex-shrink-0">
                <img 
                  src={logoUrl} 
                  alt="Logo" 
                  className="w-full h-full object-cover" 
                  crossOrigin="anonymous"
                  onError={() => setImgError(true)}
                />
              </div>
            ) : (
              <div className="w-12 h-12 md:w-16 md:h-16 rounded-xl flex-shrink-0 flex items-center justify-center text-white text-xl md:text-2xl font-bold dynamic-primary-bg shadow-sm">
                {entreprise?.nom?.charAt(0) || 'B'}
              </div>
            )}
              <div>
                <div className="flex items-center gap-2">
                    <h1 className="font-playfair font-bold text-base md:text-xl text-gray-900">{entreprise.nom}</h1>
                    {isAnnulee && (
                        <span className="px-2 py-0.5 bg-red-100 text-red-600 text-[10px] font-black rounded-full">ANNULÉE</span>
                    )}
                </div>
                {entreprise.slogan && <p className="text-[10px] md:text-xs text-gray-400 italic mt-0.5">{entreprise.slogan}</p>}
                <p className="text-[10px] md:text-xs text-gray-500 mt-2">{entreprise.adresse}</p>
                <p className="text-[10px] md:text-xs text-gray-500">{entreprise.telephone}</p>
              </div>
            </div>
            <div className="text-right">
              <div className="inline-block px-3 py-1.5 md:px-4 md:py-2 rounded-xl dynamic-primary-light-bg">
                <p className="text-[10px] md:text-xs font-semibold uppercase tracking-wider dynamic-primary-text">{entreprise.headerText}</p>
                <p className="text-sm md:text-lg font-bold text-gray-900 mt-0.5">{numFacture}</p>
              </div>
              <p className="text-[10px] md:text-xs text-gray-500 mt-2">Date : <strong>{formatDate(dateDoc)}</strong></p>
            </div>
          </div>

          {/* Client + doc info */}
          <div className="grid grid-cols-2 gap-6 mb-8">
            <div className="bg-gray-50 rounded-xl px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">Client</p>
              <p className="text-sm font-bold text-gray-800">{clientNom}</p>
              <p className="text-xs text-gray-500 mt-0.5">{clientTel}</p>
            </div>
            <div className="bg-gray-50 rounded-xl px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">
                {isCommande ? 'Détails commande' : 'Détails vente'}
              </p>
              {isCommande && commande && (
                <>
                  <p className="text-xs text-gray-600"><span className="text-gray-400">Modèle :</span> <strong>{commande.modele}</strong></p>
                  <p className="text-xs text-gray-600 mt-0.5"><span className="text-gray-400">Statut :</span> <strong>{statutLabels[commande.statut]}</strong></p>
                  {commande.coupeur && <p className="text-xs text-gray-600 mt-0.5"><span className="text-gray-400">Coupeur :</span> {commande.coupeur}</p>}
                  {commande.tailleur && <p className="text-xs text-gray-600 mt-0.5"><span className="text-gray-400">Tailleur :</span> {commande.tailleur}</p>}
                </>
              )}
              {!isCommande && vente && (
                <p className="text-xs text-gray-600"><span className="text-gray-400">Vendeur :</span> <strong>{vente.vendeur}</strong></p>
              )}
            </div>
          </div>

          {/* Items table */}
          <div className="mb-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="dynamic-primary-header-bg">
                  <th className="text-left text-xs font-semibold uppercase tracking-wider px-3 py-2 rounded-tl-lg dynamic-primary-text">Désignation</th>
                  <th className="text-center text-xs font-semibold uppercase tracking-wider px-3 py-2 dynamic-primary-text">Qté</th>
                  <th className="text-right text-xs font-semibold uppercase tracking-wider px-3 py-2 dynamic-primary-text">P.U.</th>
                  <th className="text-right text-xs font-semibold uppercase tracking-wider px-3 py-2 rounded-tr-lg dynamic-primary-text">Total</th>
                </tr>
              </thead>
              {/* Items table logic */}
              {isCommande && commande ? (
                <>
                  {commande.articles && Array.isArray(commande.articles) && commande.articles.length > 0 ? (
                    commande.articles.map((art: any, i: number) => {
                      const coutureTotal = Number(art.total_ligne) || 0;
                      const tissusTotal = (art.tissus || []).reduce((acc: number, t: any) => acc + (Number(t.total_ligne) || 0), 0);
                      const totalArt = coutureTotal + tissusTotal;
                      return (
                        <tbody key={i} className="border-b border-gray-100">
                          <tr className="bg-gray-50/50">
                            <td className="px-3 py-2 font-bold text-gray-900" colSpan={3}>
                              {art.type_habit_nom || art.nom} — {art.modele_nom || 'Sur mesure'}
                            </td>
                            <td className="px-3 py-2 text-right font-black text-gray-900">
                              {fmt(totalArt, entreprise.devise)}
                            </td>
                          </tr>
                          {/* Main d'œuvre */}
                          {Number(art.total_ligne) > 0 && (
                            <tr>
                              <td className="px-3 py-1 pl-6 text-xs text-gray-600 italic">└ Main d'œuvre (Couture)</td>
                              <td className="px-3 py-1 text-center text-xs text-gray-500">{art.quantite}</td>
                              <td className="px-3 py-1 text-right text-xs text-gray-500">{fmt(art.prix_unitaire, entreprise.devise)}</td>
                              <td className="px-3 py-1 text-right text-xs text-blue-600 font-bold">{fmt(art.total_ligne, entreprise.devise)}</td>
                            </tr>
                          )}
                          {/* Tissus */}
                          {(art.tissus || []).map((t: any, ti: number) => (
                            <tr key={ti}>
                              <td className="px-3 py-1 pl-6 text-xs text-gray-600 italic text-terre-600">
                                └ {t.is_client_cloth ? 'Tissu client' : 'Tissu boutique'}: {t.tissu_nom || t.description}
                              </td>
                              <td className="px-3 py-1 text-center text-xs text-gray-500">{t.quantite}{t.tissu_unite || 'm'}</td>
                              <td className="px-3 py-1 text-right text-xs text-gray-500">{fmt(t.prix_unitaire || 0, entreprise.devise)}</td>
                              <td className="px-3 py-1 text-right text-xs text-terre-700 font-bold">{fmt(t.total_ligne || 0, entreprise.devise)}</td>
                            </tr>
                          ))}
                        </tbody>
                      );
                    })
                  ) : (
                    <tbody className="border-b border-gray-100">
                      <tr>
                        <td className="px-3 py-2.5 text-gray-800 font-medium">Confection — {commande.modele || 'Tenue sur-mesure'}</td>
                        <td className="px-3 py-2.5 text-center text-gray-600">1</td>
                        <td className="px-3 py-2.5 text-right text-gray-600">{fmt(commande.paiement.total, entreprise.devise)}</td>
                        <td className="px-3 py-2.5 text-right font-semibold text-gray-800">{fmt(commande.paiement.total, entreprise.devise)}</td>
                      </tr>
                    </tbody>
                  )}
                </>
              ) : vente ? (
                <tbody className="border-b border-gray-100">
                  {vente.articles && Array.isArray(vente.articles) ? (
                    vente.articles.map((art: any, i: number) => (
                      <tr key={i} className="border-b border-gray-100">
                        <td className="px-3 py-2.5 text-gray-800 font-medium">{art.tissuNom || art.tissu_nom}</td>
                        <td className="px-3 py-2.5 text-center text-gray-600">{art.quantite} {art.unite || art.tissu_unite}</td>
                        <td className="px-3 py-2.5 text-right text-gray-600">{fmt(art.prixUnitaire || art.prix_unitaire, entreprise.devise)}</td>
                        <td className="px-3 py-2.5 text-right font-semibold text-gray-800">{fmt(art.total || art.total_ligne, entreprise.devise)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="px-3 py-2.5 text-gray-800 font-medium">{vente.tissu_nom || 'Tissu'}</td>
                      <td className="px-3 py-2.5 text-center text-gray-600">{vente.quantite} {vente.tissu_unite || 'm'}</td>
                      <td className="px-3 py-2.5 text-right text-gray-600">{fmt(vente.prix_unitaire || 0, entreprise.devise)}</td>
                      <td className="px-3 py-2.5 text-right font-semibold text-gray-800">{fmt(vente.total, entreprise.devise)}</td>
                    </tr>
                  )}
                </tbody>
              ) : null}
            </table>
          </div>

          {/* Totals */}
          <div className="flex justify-end mb-4 md:mb-8">
            <div className="w-full md:w-64 space-y-1.5 bg-gray-50 p-4 rounded-2xl">
              {isCommande && commande ? (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Total brut</span>
                    <span className="font-bold text-gray-900">{fmt(commande.paiement.total, entreprise.devise)}</span>
                  </div>
                  {commande.paiement.remise > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gold-600 font-semibold">Remise</span>
                      <span className="text-gold-600 font-bold">— {fmt(commande.paiement.remise, entreprise.devise)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Acompte versé</span>
                    <span className="text-green-600 font-semibold">— {fmt(commande.paiement.acompte, entreprise.devise)}</span>
                  </div>
                  <div className="h-px bg-gray-200 my-2" />
                  <div className="flex justify-between text-sm">
                    <span className="font-bold text-gray-800">Net à payer</span>
                    <span className="font-bold text-gray-900">{fmt(Number(commande.paiement.total || 0) - Number(commande.paiement.remise || 0), entreprise.devise)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-green-600 font-semibold italic">Acompte versé</span>
                    <span className="text-green-600 font-semibold">— {fmt(commande.paiement.acompte, entreprise.devise)}</span>
                  </div>
                  <div className="h-px bg-gray-200 my-1" />
                  <div className="flex justify-between text-sm">
                    <span className="font-bold text-gray-800">Reste à payer</span>
                    <span className={`font-bold text-lg ${commande.paiement.reste <= 0 ? 'text-green-600' : 'text-red-500'}`}>{fmt(commande.paiement.reste, entreprise.devise)}</span>
                  </div>
                  <div className="mt-2 pt-2 border-t border-gray-50 flex justify-between items-center">
                    <p className="text-[10px] text-gray-400 uppercase font-bold tracking-widest">Mode : {commande.paiement.modePaiement || 'N/A'}</p>
                    {commande.paiement.vendeur && (
                      <p className="text-[10px] text-gray-500 font-bold italic">Signé : {commande.paiement.vendeur}</p>
                    )}
                  </div>
                </>
              ) : vente ? (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Total brut</span>
                    <span className="font-bold text-gray-900">{fmt(vente.total, entreprise.devise)}</span>
                  </div>
                  {(vente.remise ?? 0) > 0 && (
                    <div className="flex justify-between text-sm text-gold-600 font-bold">
                      <span>Remise</span>
                      <span>— {fmt(vente.remise ?? 0, entreprise.devise)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Acompte versé</span>
                    <span className="text-green-600 font-semibold">— {fmt(vente.acompte ?? vente.montantRecu, entreprise.devise)}</span>
                  </div>
                  {(vente.monnaie ?? 0) > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Monnaie rendue</span>
                      <span className="text-gray-600">{fmt(vente.monnaie ?? 0, entreprise.devise)}</span>
                    </div>
                  )}
                  <div className="h-px bg-gray-200 my-2" />
                  <div className="flex justify-between text-sm">
                    <span className="font-bold text-gray-800">{(vente.reste ?? 0) > 0 ? 'Reste à payer' : 'Soldé'}</span>
                    <span className={`font-bold text-lg ${(vente.reste ?? 0) > 0 ? 'text-red-500' : 'text-green-600'}`}>
                      {(vente.reste ?? 0) > 0 ? fmt(vente.reste ?? 0, entreprise.devise) : 'Payé'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 text-right">Mode : {vente.modePaiement}</p>
                </>
              ) : null}
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 pt-4 text-center">
            <p className="text-xs text-gray-400 leading-relaxed">{entreprise.footerText}</p>
            <p className="text-xs text-gray-300 mt-2">{entreprise.nom} · {entreprise.adresse}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
